import { FC, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { buildTemplatesUrl } from '@/shared/lib';

import { organizationModel } from '@/entities/organization';
import { isBlockNoteTemplate, templatesModel } from '@/entities/templates';

import { DocumentTemplateForm } from '@/features/templateEditor';
import { TemplateSettingsDraft } from '@/features/templateEditor/config/types';

import styles from './TemplatesEditorPane.module.scss';

type TemplatesDocumentEditorLocationState = {
  initialSettings?: TemplateSettingsDraft;
};

type TemplatesDocumentEditorProps = {
  className?: string;
};

export const TemplatesDocumentEditor: FC<TemplatesDocumentEditorProps> = (
  props,
) => {
  const { className } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const [searchParams] = useSearchParams();

  const templateId = searchParams.get('id') ?? undefined;
  const schemaId = searchParams.get('specialty') ?? undefined;
  const locationState =
    location.state as TemplatesDocumentEditorLocationState | null;
  const initialSettings = templateId
    ? undefined
    : locationState?.initialSettings;

  const template = useAppSelector(
    templateId
      ? templatesModel.selectors.selectDocumentTemplateById(templateId)
      : () => undefined,
  );

  const schema = useAppSelector(
    schemaId
      ? templatesModel.selectors.selectEncounterSchemaById(schemaId)
      : () => undefined,
  );

  const specialtyOptions = useAppSelector(
    templatesModel.selectors.selectAllEncounterSchemas,
  );

  const isTemplateByIdLoading = useAppSelector(
    templateId
      ? templatesModel.selectors.selectDocumentTemplateByIdLoading(templateId)
      : () => false,
  );
  const templatesError = useAppSelector(
    templatesModel.selectors.selectTemplatesError,
  );
  const isSchemasLoading = useAppSelector(
    templatesModel.selectors.selectEncounterSchemasLoading,
  );

  useEffect(() => {
    if (templateId) {
      void dispatch(
        templatesModel.fetchDocumentTemplateById({
          id: templateId,
          organizationId,
        }),
      );
    }
  }, [templateId, organizationId]);

  useEffect(() => {
    if (schemaId) {
      void dispatch(
        templatesModel.fetchEncounterSchemaById({
          id: schemaId,
          organizationId,
        }),
      );
    }
  }, [schemaId, organizationId]);

  const handleSaveComplete = useCallback(() => {
    navigate(buildTemplatesUrl({ specialty: schemaId }));
  }, [schemaId]);

  if (templateId && !template && !isTemplateByIdLoading && templatesError) {
    return (
      <div className={className}>
        <FormattedMessage
          id="templates.documentEditor.loadError"
          defaultMessage="Failed to load the template"
        />
      </div>
    );
  }

  // Show the placeholder only until the template is in the store. Autosave
  // navigates to the id of the version it just created, which is already
  // stored, and unmounting the form on that refetch would drop the edits made
  // in the meantime and the caret position.
  if ((templateId && !template) || (schemaId && !schema && isSchemasLoading)) {
    return (
      <div className={className}>
        <FormattedMessage id="global.loading" defaultMessage="Loading…" />
      </div>
    );
  }

  // This form edits options; it would drop a block template's lines.
  if (template && isBlockNoteTemplate(template)) {
    return (
      <p
        className={className ?? styles.editor}
        data-component="templates-block-note-notice"
      >
        <FormattedMessage
          id="templates.documentEditor.blockNote"
          defaultMessage="“{name}” is a block note template. Its lines and blocks are edited on the Templates page, opened from a patient's Recordings, and saved there as a new version."
          values={{ name: template.name }}
        />
      </p>
    );
  }

  return (
    <DocumentTemplateForm
      className={className ?? styles.editor}
      template={template}
      initialSchemaId={schemaId ?? null}
      specialtyOptions={specialtyOptions}
      schemaLanguage={schema?.language}
      initialSettings={initialSettings}
      onSaveComplete={handleSaveComplete}
    />
  );
};
